/**
 * React Query hooks for AI Response Drafts API
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { AIDraft, AIDraftListResponse } from '@/types/aiDraft';
import {
  listAIDrafts,
  getAIDraft,
  approveAIDraft,
  rejectAIDraft,
  sendAIDraft,
} from '../aiDrafts';

// Query keys
export const aiDraftKeys = {
  all: ['ai-drafts'] as const,
  lists: () => [...aiDraftKeys.all, 'list'] as const,
  list: (status?: string) => [...aiDraftKeys.lists(), { status }] as const,
  details: () => [...aiDraftKeys.all, 'detail'] as const,
  detail: (id: string) => [...aiDraftKeys.details(), id] as const,
};

/**
 * List AI drafts with optional status filter. Polls every 30s.
 */
export function useAIDrafts(status?: string): UseQueryResult<AIDraftListResponse, Error> {
  return useQuery({
    queryKey: aiDraftKeys.list(status),
    queryFn: () => listAIDrafts(status),
    refetchInterval: 30_000,
  });
}

/**
 * Get a single AI draft with full conversation context
 */
export function useAIDraft(id: string): UseQueryResult<AIDraft, Error> {
  return useQuery({
    queryKey: aiDraftKeys.detail(id),
    queryFn: () => getAIDraft(id),
    enabled: !!id,
  });
}

/**
 * Approve a draft (optionally with edited content), then send it
 */
export function useApproveAndSendAIDraft(): UseMutationResult<
  AIDraft,
  Error,
  { id: string; draftContent?: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, draftContent }) => {
      await approveAIDraft(id, draftContent);
      return sendAIDraft(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}

/**
 * Reject a draft with a reason
 */
export function useRejectAIDraft(): UseMutationResult<
  AIDraft,
  Error,
  { id: string; reason: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => rejectAIDraft(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiDraftKeys.lists() });
    },
  });
}
